// Detection helpers for the PWA install gate.

import { isNative } from '@ah/native/runtime';

const DISMISS_KEY = 'fg.install.dismissed';

export type Platform =
  | 'ios-safari'      // Safari on iPhone/iPad — can install
  | 'ios-other'       // Chrome/Firefox/in-app browsers on iOS — must open in Safari
  | 'android-chrome'  // Chrome/Edge/Brave on Android — supports beforeinstallprompt
  | 'android-other'   // Firefox/Samsung Internet etc — menu instructions
  | 'desktop'         // not a mobile device
  | 'unknown';

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;

  const isIpadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || isIpadOS;
  const isAndroid = /Android/.test(ua);

  if (isIOS) {
    const inAppBrowser = /FBAN|FBAV|Instagram|Line|MicroMessenger|Twitter|LinkedInApp/i.test(ua);
    const isCriOS = /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    if (inAppBrowser || isCriOS) return 'ios-other';
    if (/Safari/.test(ua)) return 'ios-safari';
    return 'ios-other';
  }
  if (isAndroid) {
    if (/Chrome|EdgA|Brave/.test(ua) && !/SamsungBrowser/.test(ua)) return 'android-chrome';
    return 'android-other';
  }
  return 'desktop';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  // iOS Safari: navigator.standalone is the only reliable signal.
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true;
}

export function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // localStorage unavailable (Safari private mode); fall through silently.
  }
}

export function clearDismiss(): void {
  try {
    localStorage.removeItem(DISMISS_KEY);
  } catch {
    // ignore
  }
}

// True when we should intercept and show the install screen instead of /login.
// Desktop visitors see a QR code pointing back to this URL on their phone;
// mobile visitors see install instructions for their platform.
export function shouldShowInstallGate(): boolean {
  // Capacitor shell — the app IS installed (we're running inside the native
  // wrapper). Showing the "install" gate here would be nonsense.
  if (isNative()) return false;
  if (isStandalone()) return false;
  if (isDismissed()) return false;
  return detectPlatform() !== 'unknown';
}

// Minimal type for the install prompt event.
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}
